import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowPathIcon,
  ArchiveBoxIcon,
  PlusIcon,
  PhotoIcon,
  EyeIcon,
  PauseIcon,
  PlayIcon,
  PencilSquareIcon,
  TrashIcon,
  ArrowUturnUpIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import StatusBadge from '../components/StatusBadge';
import EmptyState from '../components/EmptyState';
import ConfirmCommandDialog from '../components/ConfirmCommandDialog';
import { fetchMyListings, publishListing, pauseListing, resumeListing, deleteListing } from '../api/marketplaceApi';
import { API_URL } from '../../../api/config';
import type { Listing, ListingDeleteBlocked } from '../types';

const SupplierListings: React.FC = () => {
  const [listings, setListings] = useState<Listing[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionId, setActionId] = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Listing | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const navigate = useNavigate();

  const load = () => {
    setLoading(true);
    fetchMyListings()
      .then(res => setListings(res.items || []))
      .catch(err => setError(err?.message || 'Failed to load listings'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handlePublish = async (id: number) => {
    setActionId(id);
    setError(null);
    try {
      await publishListing(id);
      setInfo('Listing published and visible to buyers.');
      load();
    } catch (e: any) {
      setError(e?.message || 'Failed to publish listing');
    } finally {
      setActionId(null);
    }
  };

  const handlePause = async (id: number) => {
    setActionId(id);
    setError(null);
    try {
      await pauseListing(id);
      setInfo('Listing paused and hidden from buyers.');
      load();
    } catch (e: any) {
      setError(e?.message || 'Failed to pause listing');
    } finally {
      setActionId(null);
    }
  };

  const handleResume = async (id: number) => {
    setActionId(id);
    setError(null);
    try {
      await resumeListing(id);
      setInfo('Listing resumed and is visible to buyers.');
      load();
    } catch (e: any) {
      setError(e?.message || 'Failed to resume listing');
    } finally {
      setActionId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setActionId(deleteTarget.id);
    setError(null);
    try {
      await deleteListing(deleteTarget.id);
      setListings(current => current.filter(item => item.id !== deleteTarget.id));
      setInfo('Listing deleted. Historical orders remain available.');
      setDeleteTarget(null);
    } catch (e: any) {
      const blocked = e as ListingDeleteBlocked;
      setError(blocked.code === 'LISTING_DELETE_BLOCKED' && blocked.reasons?.length
        ? `Cannot delete this listing: ${blocked.reasons.join(', ')}`
        : (e?.message || 'Failed to delete listing'));
      setDeleteTarget(null);
    } finally {
      setActionId(null);
    }
  };

  const resolveImg = (url?: string) => {
    if (!url) return null;
    return url.startsWith('http') ? url : `${API_URL}${url}`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative overflow-hidden bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 rounded-2xl p-8 text-white">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-80 h-80 bg-white rounded-full -translate-y-1/2 translate-x-1/4"></div>
        </div>
        <div className="relative flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
              <ArchiveBoxIcon className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-3xl font-extrabold tracking-tight">My Listings</h1>
              <p className="text-emerald-100 mt-1">Manage your product listings on the marketplace</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={load} className="flex items-center gap-2 px-4 py-2.5 bg-white/20 backdrop-blur-sm text-white rounded-xl hover:bg-white/30 font-medium transition-all">
              <ArrowPathIcon className="w-5 h-5" />
            </button>
            <button
              onClick={() => navigate('/marketplace/supplier/listings/new')}
              className="flex items-center gap-2 px-5 py-2.5 bg-white text-emerald-700 rounded-xl hover:bg-emerald-50 font-bold transition-all shadow-lg"
            >
              <PlusIcon className="w-5 h-5" />
              New Listing
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <ExclamationTriangleIcon className="h-5 w-5 shrink-0" />
          <span>{error}</span>
          <button type="button" onClick={() => setError(null)} className="ml-auto font-semibold">Dismiss</button>
        </div>
      )}
      {info && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm font-medium text-emerald-800">
          {info}
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="relative">
            <div className="w-12 h-12 border-4 border-emerald-200 rounded-full"></div>
            <div className="w-12 h-12 border-4 border-emerald-600 rounded-full border-t-transparent animate-spin absolute top-0 left-0"></div>
          </div>
        </div>
      ) : listings.length === 0 ? (
        <EmptyState
          icon={<ArchiveBoxIcon className="w-12 h-12" />}
          title="No listings yet"
          description="Create your first listing to start selling on the marketplace."
          action={{ label: 'Create Listing', onClick: () => navigate('/marketplace/supplier/listings/new') }}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {listings.map((l) => {
            const img = resolveImg(l.image_url);
            return (
              <div key={l.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition-all overflow-hidden group">
                {/* Image */}
                <div className="relative h-48 bg-gradient-to-br from-emerald-50 to-teal-50 overflow-hidden">
                  {img ? (
                    <img src={img} alt={l.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <PhotoIcon className="w-16 h-16 text-emerald-200" />
                    </div>
                  )}
                  <div className="absolute top-3 right-3">
                    <StatusBadge status={l.status} size="sm" />
                  </div>
                  {l.traceability_enabled && (
                    <div className="absolute top-3 left-3">
                      <span className="px-2 py-1 bg-blue-500 text-white text-xs font-bold rounded-full">Traceable</span>
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-5">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className="font-extrabold text-gray-900 text-sm leading-tight line-clamp-2">{l.title}</h3>
                    {l.grade && (
                      <span className="shrink-0 px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs font-bold">{l.grade}</span>
                    )}
                  </div>

                  <p className="text-xs text-gray-500 mb-3 line-clamp-2">{l.description || 'No description'}</p>

                  {l.certification && (
                    <div className="mb-3">
                      <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full text-xs font-semibold">{l.certification}</span>
                    </div>
                  )}

                  <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                    <div>
                      <p className="text-lg font-extrabold text-emerald-600">{l.currency} {Number(l.price_per_unit).toLocaleString()}</p>
                      <p className="text-xs text-gray-400">per {l.commercial_unit}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-gray-700">{l.available_quantity} {l.commercial_unit}</p>
                      <p className="text-xs text-gray-400">available</p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 mt-4">
                    {l.status === 'draft' && (
                      <button
                        onClick={() => handlePublish(l.id)}
                        disabled={actionId === l.id}
                        className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-emerald-600 text-white rounded-xl text-xs font-bold hover:bg-emerald-700 disabled:opacity-50 transition-all"
                      >
                        <PlayIcon className="w-3.5 h-3.5" />
                        Publish
                      </button>
                    )}
                    {l.status === 'active' && (
                      <button
                        onClick={() => handlePause(l.id)}
                        disabled={actionId === l.id}
                        className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-amber-50 text-amber-700 border border-amber-200 rounded-xl text-xs font-bold hover:bg-amber-100 disabled:opacity-50 transition-all"
                      >
                        <PauseIcon className="w-3.5 h-3.5" />
                        Pause
                      </button>
                    )}
                    {l.status === 'paused' && (
                      <button
                        onClick={() => handleResume(l.id)}
                        disabled={actionId === l.id}
                        className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-xl text-xs font-bold hover:bg-emerald-100 disabled:opacity-50 transition-all"
                      >
                        <ArrowUturnUpIcon className="w-3.5 h-3.5" />
                        Resume
                      </button>
                    )}
                    <button
                      onClick={() => navigate(`/marketplace/supplier/listings/${l.id}`)}
                      className="flex items-center justify-center gap-1.5 px-3 py-2 bg-gray-100 text-gray-700 rounded-xl text-xs font-bold hover:bg-gray-200 transition-all"
                    >
                      <EyeIcon className="w-3.5 h-3.5" />
                      View
                    </button>
                    <button
                      onClick={() => navigate(`/marketplace/supplier/listings/${l.id}/edit`)}
                      className="flex items-center justify-center p-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all"
                      title="Edit listing"
                      aria-label={`Edit ${l.title}`}
                    >
                      <PencilSquareIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setDeleteTarget(l)}
                      disabled={actionId === l.id || l.status === 'active'}
                      className="flex items-center justify-center p-2 bg-red-50 text-red-700 border border-red-200 rounded-xl hover:bg-red-100 disabled:opacity-40 transition-all"
                      title={l.status === 'active' ? 'Pause before deleting' : 'Delete listing'}
                      aria-label={`Delete ${l.title}`}
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ConfirmCommandDialog
        open={!!deleteTarget}
        title="Delete this listing?"
        description={deleteTarget
          ? `"${deleteTarget.title}" will be removed from the marketplace. Historical orders and sales records will be preserved.`
          : ''}
        confirmLabel="Delete Listing"
        variant="danger"
        loading={actionId === deleteTarget?.id}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
};

export default SupplierListings;
